<template>
  <DSFormField
    :id="id"
    :name="props.name"
    :label="formFieldLabel"
    :inline-edit="props.inlineEdit && !props.richEditor"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :required="props.required"
    :disabled="props.disabled"
    :error-messages="dsFormField.errorMessages.value"
  >
    <DSBaseInput
      v-if="!props.richEditor"
      :id="id"
      ref="textareaRef"
      v-model="modelValue"
      type="text"
      :use-text-area="true"
      :class="DS_TEXTAREA_CLASSES.Root"
      data-cy="ds-textarea"
      :name="props.name"
      :placeholder="props.placeholder"
      :inline-edit="props.inlineEdit"
      :rows="props.rows"
      :disabled="props.disabled"
      :readonly="props.readonly"
      :required="props.required"
      :autocomplete="props.autocomplete"
      :maxlength="props.maxlength"
      :minlength="props.minlength"
      :has-error="hasErrors"
      :clearable="false"
      :aria-invalid="hasErrors ? 'true' : undefined"
      @update:model-value="dsFormField.clearFieldErrors"
      @focus="onFocus"
      @blur="onBlur"
    />
    <DSTextareaRichEditor
      v-else
      :id="id"
      ref="textareaRef"
      v-model="modelValue"
      :rows="props.rows"
      :name="props.name"
      :placeholder="props.placeholder"
      :maxlength="props.maxlength"
      :minlength="props.minlength"
      :disabled="props.disabled"
      :readonly="props.readonly"
      :required="props.required"
      :autocomplete="props.autocomplete"
      :has-error="hasErrors"
      :rich-editor-config="props.richEditorConfig"
      @update:model-value="dsFormField.clearFieldErrors"
      @focus="onFocus"
      @blur="onBlur"
    />
  </DSFormField>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { DSFocusable } from "@/src/design_system/shared/types/expose";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { DS_TEXTAREA_CLASSES } from "@/src/design_system/form_elements/textarea/constants/classes";
import DSTextareaRichEditor from "@/src/design_system/form_elements/textarea/components/DSTextareaRichEditor.vue";
import type { DSTextareaEmits } from "@/src/design_system/form_elements/textarea/types/emits";
import type { DSTextareaModelValue, DSTextareaProps } from "@/src/design_system/form_elements/textarea/types/props";

const props = withDefaults(defineProps<DSTextareaProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  rows: 3,
  disabled: false,
  readonly: false,
  required: false,
  tooltip: undefined,
  richEditor: false,
  richEditorConfig: () => ({}),
  errorMessages: undefined,
  rules: undefined,
});

const modelValue = defineModel<DSTextareaModelValue>({ default: "" });

const emit = defineEmits<DSTextareaEmits>();

const textareaRef = ref<DSFocusable>();

const { focus, blur } = useDSInput(textareaRef);

const dsFormField = useDSFormField<DSTextareaModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: modelValue,
  rules: computed(() => props.rules),
});

const { id } = useDSFormFieldId({ props });

const hasErrors = computed(() => !!dsFormField.errorMessages.value.length);

const formFieldLabel = computed(() => props.label ?? undefined);

const onFocus = (event: FocusEvent) => {
  emit("focus", event);
};

const onBlur = (event: FocusEvent) => {
  emit("blur", event);
};

defineExpose({
  focus,
  blur,
});
</script>
